import type { Turn } from '@chessmate/protocol';
import { allSquares } from './power.js';

/**
 * The row where a man becomes a king, as the rank a human would write it.
 *
 * This exists as its own module because of a trap that is invisible from inside
 * `checkers.ts`. The power map scores **both** rank 8 and rank 1 at zero —
 * they are the outermost ring, three steps from the centre. For chess that is
 * fine. For checkers, one of those two rows is where your man crowns, and it is
 * the single most valuable row on the board. A child looking at the power
 * overlay in a checkers game is therefore told, in the app's own colours, that
 * the goal is worthless.
 *
 * The fix is **not** to bend `powerOf` — that would need a variant parameter
 * and would fork a function whose whole virtue is that it is variant-agnostic.
 * It is to mark the row separately, next to the wash, so the two claims sit
 * side by side: the centre is where fights happen *and* this edge is where you
 * become a king.
 *
 * The fact was already here — `checkers.ts` has had a private
 * `promotionRank` since the engine was written — but it was expressed as a
 * **rankIndex** (row 0 for white), not a rank. A UI layer reading that directly
 * would light the wrong end of the board, and there is no type that stops you.
 * This module is the one place the rank is written down, and `checkers.ts` now
 * derives from it rather than keeping a second copy.
 */

/**
 * Algebraic rank a man of `color` crowns on. White moves up the board and crowns
 * on rank 8; black crowns on rank 1.
 */
export const PROMOTION_RANK: Record<Turn, 1 | 8> = { w: 8, b: 1 };

/** The eight squares of `color`'s promotion row, a-file to h-file. */
export function promotionRow(color: Turn): string[] {
  return allSquares().filter((square) => Number(square[1]) === PROMOTION_RANK[color]);
}

/**
 * True when `color` promotes by moving a man onto `square`.
 *
 * Answered from the coordinate alone, so it holds for an empty square — which
 * is what an overlay wants. A *piece* is only promoted once it has actually got
 * there; that half is the engine's business, not this module's.
 */
export function isPromotionSquare(color: Turn, square: string): boolean {
  return Number(square[1]) === PROMOTION_RANK[color];
}
